import React, { useEffect, useRef, useState } from 'react';
import Vara from 'vara';

/**
 * The name, written rather than set.
 *
 * Vara draws SVG stroke paths from a handwriting font, so the letters are
 * actually formed the way a pen forms them — which is what my hand-authored
 * squiggles could never be. The font JSON is served from /fonts, not a CDN.
 *
 * The visible drawing is aria-hidden; the accessible name lives in the
 * screen-reader-only span in the <h1>, so nothing depends on the animation.
 */
let instanceCount = 0;

export default function HandwrittenName({ text = 'Kazuya Hirotsu', font = 'PacificoSLO' }) {
  const hostRef = useRef(null);
  const idRef = useRef(`handwritten-${++instanceCount}`);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let cancelled = false;

    // Vara measures its container and inserts line breaks to fit, so a narrow
    // column splits the name across two lines. Give it a very wide box to lay
    // out in — absolutely positioned, so it costs the page no space — then in
    // ready() swap the fixed size for a viewBox and let CSS scale the one line
    // down to whatever room there actually is.
    const fontSize = 72;
    host.innerHTML = '';
    host.style.position = 'absolute';
    host.style.width = '2400px';

    function fitToOneLine() {
      const svg = host.querySelector('svg');
      if (!svg) return;
      let box;
      try {
        box = svg.getBBox();
      } catch (err) {
        return;
      }
      if (!box || !box.width) return;
      const pad = 6;
      svg.setAttribute(
        'viewBox',
        `${box.x - pad} ${box.y - pad} ${box.width + pad * 2} ${box.height + pad * 2}`
      );
      svg.setAttribute('preserveAspectRatio', 'xMinYMid meet');
      svg.removeAttribute('height');
      svg.setAttribute('width', '100%');
      svg.style.width = '100%';
      svg.style.height = 'auto';
      host.style.position = '';
      host.style.width = '';

    }

    // Vara resolves its container with document.querySelector, so it needs a
    // selector — handing it the node itself silently does nothing.
    let instance;
    try {
      instance = new Vara(
        `#${idRef.current}`,
        `${process.env.PUBLIC_URL}/fonts/${font}.json`,
        [
          {
            text,
            fontSize,
            strokeWidth: 1.4,
            color: '#22303A',
            duration: reduced ? 0 : 2400,
            textAlign: 'left',
            autoAnimation: true,
            letterSpacing: 1,
          },
        ],
        { fontSize, strokeWidth: 1.4, color: '#22303A' }
      );
    } catch (e) {
      if (!cancelled) setFailed(true);
      host.style.position = '';
      host.style.width = '';
      return;
    }

    if (instance && typeof instance.ready === 'function') {
      instance.ready(function () {
        if (!cancelled) fitToOneLine();
      });
    }

    // If the font never arrives, fall back to type rather than an empty hero.
    const guard = setTimeout(() => {
      if (cancelled) return;
      if (!host.querySelector('svg')) setFailed(true);
      else fitToOneLine();          // belt and braces if ready() never fires
    }, 4000);

    return () => {
      cancelled = true;
      clearTimeout(guard);
      host.innerHTML = '';   // Vara has no destroy(); clearing the node is the teardown
      host.style.position = '';
      host.style.width = '';
    };
  }, [text, font]);

  return (
    <>
      <span className="sr-only">{text}</span>
      <div
        className={`hand${failed ? ' hand-failed' : ''}`}
        id={idRef.current}
        ref={hostRef}
        aria-hidden="true"
      />
      {failed && <span className="hand-fallback" aria-hidden="true">{text}</span>}
    </>
  );
}
